import { StatusCodes, getReasonPhrase } from "http-status-codes";

/**
 * Get a user-friendly description for an HTTP status code
 */
export function getStatusDescription(code) {
  const codeNum = parseInt(code, 10);

  const descriptions = {
    // 2xx Success
    [StatusCodes.OK]: "Everything worked as expected.",
    [StatusCodes.CREATED]: "Resource created successfully.",
    [StatusCodes.ACCEPTED]: "Request accepted for processing.",
    [StatusCodes.NO_CONTENT]: "Request successful with no content to return.",

    // 3xx Redirection
    [StatusCodes.MOVED_PERMANENTLY]: "This resource has moved permanently.",
    [StatusCodes.FOUND]: "Resource found at a different location.",
    [StatusCodes.SEE_OTHER]: "See another location for this resource.",
    [StatusCodes.NOT_MODIFIED]: "Resource has not been modified.",
    [StatusCodes.TEMPORARY_REDIRECT]: "Resource temporarily moved.",

    // 4xx Client Errors
    [StatusCodes.BAD_REQUEST]:
      "The request could not be understood or was missing required parameters.",
    [StatusCodes.UNAUTHORIZED]:
      "You need to be authenticated to access this resource.",
    [StatusCodes.PAYMENT_REQUIRED]:
      "Payment is required to access this resource.",
    [StatusCodes.FORBIDDEN]:
      "You don't have permission to access this resource.",
    [StatusCodes.NOT_FOUND]: "The page you're looking for doesn't exist.",
    [StatusCodes.METHOD_NOT_ALLOWED]:
      "This method is not allowed for the requested resource.",
    [StatusCodes.NOT_ACCEPTABLE]:
      "The requested resource cannot generate acceptable content.",
    [StatusCodes.REQUEST_TIMEOUT]: "The request took too long to process.",
    [StatusCodes.CONFLICT]:
      "The request conflicts with the current state of the server.",
    [StatusCodes.GONE]: "This resource is no longer available.",
    [StatusCodes.LENGTH_REQUIRED]: "Content-Length header is required.",
    [StatusCodes.PRECONDITION_FAILED]:
      "Precondition in request headers failed.",
    [StatusCodes.PAYLOAD_TOO_LARGE]: "The request payload is too large.",
    [StatusCodes.URI_TOO_LONG]: "The request URI is too long.",
    [StatusCodes.UNSUPPORTED_MEDIA_TYPE]: "The media type is not supported.",
    [StatusCodes.RANGE_NOT_SATISFIABLE]:
      "The requested range is not satisfiable.",
    [StatusCodes.EXPECTATION_FAILED]:
      "Expectation given in Expect header could not be met.",
    [StatusCodes.IM_A_TEAPOT]: "I'm a teapot (RFC 2324).",
    [StatusCodes.UNPROCESSABLE_ENTITY]:
      "The request was well-formed but contains semantic errors.",
    [StatusCodes.LOCKED]: "The resource is locked.",
    [StatusCodes.FAILED_DEPENDENCY]:
      "The request failed due to failure of a previous request.",
    [StatusCodes.TOO_MANY_REQUESTS]:
      "You've sent too many requests. Please slow down.",

    // 5xx Server Errors
    [StatusCodes.INTERNAL_SERVER_ERROR]:
      "The server encountered an error processing your request.",
    [StatusCodes.NOT_IMPLEMENTED]: "This functionality is not yet implemented.",
    [StatusCodes.BAD_GATEWAY]:
      "Invalid response received from upstream server.",
    [StatusCodes.SERVICE_UNAVAILABLE]:
      "The service is temporarily unavailable. Please try again later.",
    [StatusCodes.GATEWAY_TIMEOUT]:
      "The gateway timed out waiting for a response.",
    [StatusCodes.HTTP_VERSION_NOT_SUPPORTED]: "HTTP version not supported.",
  };

  return descriptions[codeNum] || "An unexpected error occurred.";
}

/**
 * Get emoji for status code category
 */
export function getStatusEmoji(code) {
  const codeNum = parseInt(code, 10);

  if (codeNum >= 200 && codeNum < 300) return "✅";
  if (codeNum >= 300 && codeNum < 400) return "🔀";
  if (codeNum >= 400 && codeNum < 500) return "⚠️";
  if (codeNum >= 500 && codeNum < 600) return "🔥";

  return "❓";
}

/**
 * Check if status code is an error
 */
export function isErrorStatus(code) {
  const codeNum = parseInt(code, 10);
  return codeNum >= 400;
}

/**
 * Get status category
 */
export function getStatusCategory(code) {
  const codeNum = parseInt(code, 10);

  if (codeNum >= 200 && codeNum < 300) return "Success";
  if (codeNum >= 300 && codeNum < 400) return "Redirection";
  if (codeNum >= 400 && codeNum < 500) return "Client Error";
  if (codeNum >= 500 && codeNum < 600) return "Server Error";

  return "Unknown";
}

export { StatusCodes, getReasonPhrase };
